import useSWR, { mutate } from "swr"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import type { THotelOtaRoomType } from "../HotelOtaRoomTypes"

export type TOtaMealCategory = "CP" | "MAP" | "AP" | "EP"

export type TOtaMealPlan = {
	id: number
	name: string
	description: string | null
	// Null for a plan outside the standard categories, e.g. a jungle safari plan
	meal_category: TOtaMealCategory | null
}

export const OTA_MEAL_CATEGORIES: Array<{
	value: TOtaMealCategory
	meals: string
}> = [
	{ value: "CP", meals: "Breakfast" },
	{ value: "MAP", meals: "Breakfast + Lunch/Dinner" },
	{ value: "AP", meals: "Breakfast + Lunch + Dinner" },
	{ value: "EP", meals: "Room only" },
]

// A hotel's meal plan, with the room types it is sold with
export type THotelOtaMealPlan = TOtaMealPlan & {
	ota_room_types: THotelOtaRoomType[]
}

export type TOtaMealPlanPayload = Omit<TOtaMealPlan, "id">

export function hotelOtaMealPlansXHR(xhr: XHRInstance) {
	return {
		async getOtaMealPlans(
			hotelId: string | number,
			params?: Record<string, unknown>
		): Promise<IListResponse<TOtaMealPlan>> {
			return xhr
				.get(`/hotels/${hotelId}/ota-meal-plans`, { params })
				.then((resp) => resp.data)
		},
		async createOtaMealPlan(
			hotelId: string | number,
			data: TOtaMealPlanPayload
		): Promise<TOtaMealPlan> {
			return xhr
				.post(`/hotels/${hotelId}/ota-meal-plans`, data)
				.then((resp) => resp.data.data)
		},
		async updateOtaMealPlan(
			hotelId: string | number,
			id: number,
			data: TOtaMealPlanPayload
		): Promise<TOtaMealPlan> {
			return xhr
				.put(`/hotels/${hotelId}/ota-meal-plans/${id}`, data)
				.then((resp) => resp.data.data)
		},
		async deleteOtaMealPlan(id: number): Promise<{ message: string }> {
			return xhr.delete(`/ota-meal-plans/${id}`).then((resp) => resp.data.data)
		},
	}
}

function allHotelOtaMealPlansKey(hotelId: string | number) {
	return `/api/hotels/${hotelId}/ota-meal-plans?all`
}

// Every meal plan of a hotel in one go, for pickers (e.g. the room type form).
export function useHotelOtaMealPlans(hotelId: string | number) {
	const xhr = useXHR()
	const { data, error } = useSWR(allHotelOtaMealPlansKey(hotelId), () =>
		hotelOtaMealPlansXHR(xhr)
			.getOtaMealPlans(hotelId, { limit: 1000 })
			.then((resp) => resp.data)
	)
	return {
		mealPlans: data,
		isFetching: !data && !error,
		error,
	}
}

export function refreshHotelOtaMealPlans(hotelId: string | number) {
	return mutate(allHotelOtaMealPlansKey(hotelId))
}
